import React from 'react';
import { X, CheckCircle, Star } from 'lucide-react';

interface GuestSignupPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onSignup: () => void;
  clipCount?: number;
}

const GuestSignupPrompt: React.FC<GuestSignupPromptProps> = ({
  isOpen,
  onClose,
  onSignup,
  clipCount = 1
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 animate-fade-up border-4 border-red-500">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 rounded-t-xl text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
              <Star className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">🎉 Success! {clipCount} Clip{clipCount !== 1 ? 's' : ''} Generated!</h2>
              <p className="text-red-100 text-sm">Your AI-powered highlights are ready</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 bg-white rounded-b-xl">
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircle className="h-5 w-5 text-red-500" />
              <span className="font-medium text-gray-900">Free trial complete!</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              You've experienced the power of AI-generated clips. Create an account to save your work, 
              generate unlimited clips, and access pro features.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-gray-900 font-semibold text-sm mb-3">Create a free account to unlock:</p>
            <div className="space-y-2.5">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">∞ Unlimited Video Uploads</p>
                  <p className="text-gray-600 text-xs">No limits. Upload and process as many videos as you want</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">💾 Keep Clips Forever</p>
                  <p className="text-gray-600 text-xs">Permanent storage - your clips never expire</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">📤 All Export Formats</p>
                  <p className="text-gray-600 text-xs">Download as MP4, WebM, or GIF in HD quality</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">🤖 Advanced AI Analysis</p>
                  <p className="text-gray-600 text-xs">Better highlights with emotion & face detection</p>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={onSignup}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-4 px-6 rounded-xl font-bold text-base hover:from-red-600 hover:to-red-700 transition-all duration-200 transform hover:scale-[1.02] shadow-xl"
            >
              Create Free Account - 100% Free Forever
            </button>
            
            <button
              onClick={onClose}
              className="w-full text-gray-600 py-2 px-4 rounded-xl font-medium hover:text-gray-900 hover:bg-gray-100 transition-colors text-sm border border-gray-300"
            >
              Maybe Later
            </button>
          </div>

          {/* Warning Note */}
          <div className="mt-4 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
            <p className="text-red-800 text-xs text-center font-semibold mb-1">
              ⚠️ Your clips will be deleted after this session!
            </p>
            <p className="text-red-700 text-xs text-center">
              Sign up now to keep them permanently. Takes only 30 seconds.
            </p>
          </div>
          
          <p className="text-gray-500 text-xs text-center mt-3">
            ✨ No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestSignupPrompt;
